import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { prestataireService } from '../services/api';

const SERVICES_MAP = {
  MECANIQUE_MOTO:    '🏍️ Moto',
  MECANIQUE_VOITURE: '🚗 Voiture',
  VULCANISATEUR:     '🔩 Vulcano',
  ELECTRICITE_AUTO:  '⚡ Électricité',
  CARROSSERIE:       '🎨 Carrosserie',
  CLIMATISATION:     '❄️ Clim',
  VIDANGE_ENTRETIEN: '🔧 Vidange',
  PIECES_DETACHEES:  '⚙️ Pièces',
};

const CITIES = ['Cotonou','Porto-Novo'];

export default function PrestatairesPage() {
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState({ data: [], pagination: { total: 0, totalPages: 1 } });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    service:     searchParams.get('service')     || '',
    city:        searchParams.get('city')        || '',
    search:      searchParams.get('search')      || '',
    isAvailable: searchParams.get('isAvailable') || '',
    isVerified:  searchParams.get('isVerified')  || '',
    sortBy: 'ratingAvg',
    page: 1,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await prestataireService.getAll(filters);
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key, val) => setFilters(p => ({ ...p, [key]: val, page: 1 }));
  const setPage = (p) => setFilters(prev => ({ ...prev, page: p }));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-light)', paddingTop: '80px' }}>

      {/* ── HEADER ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--blue-dark) 0%, #0D1B2A 100%)',
        padding: '3rem 0 2.5rem',
        position: 'relative', overflow: 'hidden',
      }}>
        <motion.div style={{
          position: 'absolute', fontSize: 400, right: '-8%', top: '-20%',
          color: 'rgba(255,107,0,0.04)', lineHeight: 1, pointerEvents: 'none',
        }}
          animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
        >⚙</motion.div>
        <div className="container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="section-tag" style={{ background: 'rgba(255,107,0,0.15)', borderColor: 'rgba(255,107,0,0.4)' }}>
              {result.pagination?.total || 0} PRESTATAIRES INSCRITS
            </span>
            <h1 style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(2.8rem, 7vw, 5.5rem)',
              color: 'white', letterSpacing: '2px',
              lineHeight: 0.9, marginTop: '0.8rem',
            }}>
              TROUVEZ VOTRE<br />
              <span style={{ color: 'var(--orange)' }}>MÉCANICIEN</span>
            </h1>
          </motion.div>
        </div>
      </div>

      {/* ── FILTERS BAR ── */}
      <div style={{
        background: 'white', borderBottom: '1px solid var(--gray-mid)',
        padding: '1rem 0', position: 'sticky', top: '70px', zIndex: 100,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div className="container" style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', alignItems: 'center' }}>

          {/* Search */}
          <div style={{
            display: 'flex', flex: '1 1 200px', border: '2px solid var(--gray-mid)',
            borderRadius: '8px', overflow: 'hidden', background: 'white',
            transition: 'border-color 0.2s',
          }}
            onFocusCapture={e => e.currentTarget.style.borderColor = 'var(--orange)'}
            onBlurCapture={e => e.currentTarget.style.borderColor = 'var(--gray-mid)'}
          >
            <div style={{ padding: '0 1rem', display: 'flex', alignItems: 'center', color: 'var(--gray-text)' }}>
              <Search size={16} />
            </div>
            <input
              style={{ border: 'none', outline: 'none', padding: '0.75rem 0.5rem', flex: 1, fontSize: '0.9rem' }}
              value={filters.search}
              onChange={e => setFilter('search', e.target.value)}
              placeholder="Nom, spécialité, quartier..."
            />
          </div>

          <select className="form-select" style={{ width: 'auto', minWidth: '170px' }}
            value={filters.service} onChange={e => setFilter('service', e.target.value)}>
            <option value="">Tous les services</option>
            {Object.entries(SERVICES_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>

          <select className="form-select" style={{ width: 'auto', minWidth: '140px' }}
            value={filters.city} onChange={e => setFilter('city', e.target.value)}>
            <option value="">Toutes les villes</option>
            {CITIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <select className="form-select" style={{ width: 'auto', minWidth: '160px' }}
            value={filters.sortBy} onChange={e => setFilter('sortBy', e.target.value)}>
            <option value="ratingAvg">Mieux notés</option>
            <option value="ratingCount">Plus d'avis</option>
            <option value="newest">Nouveaux</option>
            <option value="priceAsc">Prix croissant</option>
          </select>

          {[
            ['isAvailable', 'true', '🟢 Disponibles'],
            ['isVerified',  'true', '✅ Vérifiés'],
          ].map(([key, val, label]) => (
            <button key={key}
              onClick={() => setFilter(key, filters[key] === val ? '' : val)}
              style={{
                padding: '0.7rem 1.2rem',
                border: `2px solid ${filters[key] === val ? 'var(--orange)' : 'var(--gray-mid)'}`,
                background: filters[key] === val ? 'rgba(255,107,0,0.08)' : 'white',
                color: filters[key] === val ? 'var(--orange)' : 'var(--gray-text)',
                borderRadius: '8px', fontFamily: 'var(--font-cond)',
                fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                transition: 'all 0.2s', whiteSpace: 'nowrap',
              }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── GRID ── */}
      <div className="container" style={{ padding: '2.5rem 1.5rem 5rem' }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '1.5rem' }}>
            {[...Array(9)].map((_, i) => (
              <div key={i} className="skeleton" style={{ borderRadius: 16, height: 340 }} />
            ))}
          </div>
        ) : result.data?.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ textAlign: 'center', padding: '6rem 2rem' }}>
            <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>🔍</div>
            <h3 style={{
              fontFamily: 'var(--font-display)', fontSize: '2.5rem',
              color: 'var(--blue-dark)', letterSpacing: '1px',
            }}>AUCUN RÉSULTAT</h3>
            <p style={{ color: 'var(--gray-text)', marginTop: '0.8rem' }}>
              Essayez de modifier vos filtres ou revenez plus tard.
            </p>
            <button className="btn btn-primary" style={{ marginTop: '1.5rem' }}
              onClick={() => setFilters({ service: '', city: '', search: '', isAvailable: '', isVerified: '', sortBy: 'ratingAvg', page: 1 })}>
              Réinitialiser les filtres
            </button>
          </motion.div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))',
              gap: '1.5rem',
            }}>
              {result.data.map((p, i) => (
                <motion.div key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.05, 0.4) }}>
                  <PrestataireCard p={p} />
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {result.pagination?.totalPages > 1 && (
              <div style={{
                display: 'flex', justifyContent: 'center',
                alignItems: 'center', gap: '0.5rem', marginTop: '3rem',
              }}>
                <button
                  onClick={() => setPage(Math.max(1, filters.page - 1))}
                  disabled={filters.page === 1}
                  className="btn btn-ghost btn-sm"
                  style={{ border: '2px solid var(--gray-mid)' }}
                >
                  <ChevronLeft size={18} />
                </button>

                {[...Array(result.pagination.totalPages)].map((_, i) => {
                  const p = i + 1;
                  if (p === 1 || p === result.pagination.totalPages ||
                    Math.abs(p - filters.page) <= 2) {
                    return (
                      <button key={p} onClick={() => setPage(p)} style={{
                        width: 40, height: 40, border: `2px solid ${filters.page === p ? 'var(--orange)' : 'var(--gray-mid)'}`,
                        background: filters.page === p ? 'var(--orange)' : 'white',
                        color: filters.page === p ? 'white' : 'var(--blue-dark)',
                        borderRadius: 8, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s',
                      }}>{p}</button>
                    );
                  }
                  if (Math.abs(p - filters.page) === 3) return <span key={p} style={{ color: 'var(--gray-text)' }}>…</span>;
                  return null;
                })}

                <button
                  onClick={() => setPage(Math.min(result.pagination.totalPages, filters.page + 1))}
                  disabled={filters.page === result.pagination.totalPages}
                  className="btn btn-ghost btn-sm"
                  style={{ border: '2px solid var(--gray-mid)' }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function PrestataireCard({ p }) {
  const initials = `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`;

  return (
    <Link to={`/prestataires/${p.id}`} style={{ display: 'block', textDecoration: 'none' }}>
      <motion.div
        whileHover={{ y: -6, boxShadow: '0 24px 60px rgba(26,59,93,0.16)' }}
        style={{
          background: 'white', borderRadius: 16, overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
          border: '1px solid rgba(26,59,93,0.06)',
          transition: 'all 0.3s', height: '100%',
        }}
      >
        {/* Photo banner */}
        <div style={{
          height: 160, background: 'linear-gradient(135deg, #1A3B5D, #0D1B2A)',
          position: 'relative', overflow: 'hidden',
        }}>
          {p.photos?.[0] ? (
            <img src={p.photos[0].photoUrl} alt="Réalisation"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div style={{
              width: '100%', height: '100%', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontSize: '5rem', color: 'rgba(255,107,0,0.12)',
            }}>🔧</div>
          )}
          {/* Badges */}
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6 }}>
            {p.isVerified && (
              <span style={{
                background: 'var(--orange)', color: 'white',
                padding: '0.25rem 0.65rem', borderRadius: 20,
                fontSize: '0.7rem', fontFamily: 'var(--font-cond)',
                fontWeight: 700, letterSpacing: '0.5px',
              }}>✅ VÉRIFIÉ</span>
            )}
          </div>
          {p.isAvailable && (
            <div style={{
              position: 'absolute', top: 10, right: 10,
              background: 'rgba(16,185,129,0.92)', color: 'white',
              padding: '0.25rem 0.65rem', borderRadius: 20,
              fontSize: '0.7rem', fontFamily: 'var(--font-cond)',
              fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <span style={{
                width: 6, height: 6, borderRadius: '50%',
                background: 'white', animation: 'pulse 1.5s infinite',
                display: 'inline-block',
              }} />
              DISPO
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: '1.4rem' }}>
          <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start', marginBottom: '1rem' }}>
            {p.profilePhotoUrl ? (
              <img src={p.profilePhotoUrl} alt={p.firstName}
                style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, border: '2px solid var(--orange)' }} />
            ) : (
              <div style={{
                width: 50, height: 50, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, var(--orange), #CC5500)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.1rem',
                boxShadow: '0 4px 12px rgba(255,107,0,0.3)',
              }}>{initials}</div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'var(--font-cond)', fontSize: '1.05rem',
                fontWeight: 700, color: 'var(--blue-dark)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {p.firstName} {p.lastName}
              </div>
              {p.shopName && (
                <div style={{ fontSize: '0.78rem', color: 'var(--orange)', fontWeight: 600 }}>
                  {p.shopName}
                </div>
              )}
            </div>
          </div>

          {/* Location + Rating */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.8rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', color: 'var(--gray-text)' }}>
              <MapPin size={12} style={{ color: 'var(--orange)' }} />
              {p.city}, {p.neighborhood}
            </div>
            {p.ratingCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem' }}>
                <Star size={12} style={{ color: '#FBBF24', fill: '#FBBF24' }} />
                <strong style={{ color: 'var(--blue-dark)' }}>{p.ratingAvg?.toFixed(1)}</strong>
                <span style={{ color: 'var(--gray-text)' }}>({p.ratingCount} avis)</span>
              </div>
            )}
          </div>

          {/* Services tags */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '1rem' }}>
            {p.services?.slice(0, 3).map(s => (
              <span key={s.service} style={{
                padding: '0.2rem 0.55rem',
                background: 'rgba(26,59,93,0.06)',
                border: '1px solid rgba(26,59,93,0.1)',
                borderRadius: 4, fontSize: '0.7rem',
                color: 'var(--blue-dark)', fontFamily: 'var(--font-cond)',
                fontWeight: 600, letterSpacing: '0.5px',
              }}>
                {SERVICES_MAP[s.service] || s.service}
              </span>
            ))}
            {p.services?.length > 3 && (
              <span style={{ padding: '0.2rem 0.55rem', background: 'rgba(255,107,0,0.08)', borderRadius: 4, fontSize: '0.7rem', color: 'var(--orange)', fontFamily: 'var(--font-cond)', fontWeight: 600 }}>
                +{p.services.length - 3}
              </span>
            )}
          </div>

          {/* Price */}
          {p.priceMin && (
            <div style={{ fontSize: '0.82rem', color: 'var(--gray-text)', marginBottom: '1rem' }}>
              À partir de{' '}
              <strong style={{ color: 'var(--blue-dark)', fontSize: '0.95rem' }}>
                {p.priceMin.toLocaleString('fr')} FCFA
              </strong>
            </div>
          )}

          {/* CTA */}
          <div style={{
            background: 'var(--blue-dark)', color: 'white',
            padding: '0.8rem', borderRadius: 8, textAlign: 'center',
            fontFamily: 'var(--font-cond)', fontWeight: 700, fontSize: '0.85rem',
            letterSpacing: '1.5px', transition: 'background 0.2s',
          }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--orange)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--blue-dark)'}
          >
            VOIR LE PROFIL →
          </div>
        </div>
      </motion.div>
    </Link>
  );
}
